import React, { useState } from 'react';
import { CollegePrepProvider } from '../../contexts/CollegePrepContext';
import InterestProfile from './InterestProfile';
import ProgramCollection from './ProgramCollection';
import ResearchTools from './ResearchTools';
import TimelinePlanning from './TimelinePlanning';
import EssayBuilder from './EssayBuilder/EssayBuilder';

const CollegePrep = () => {
  const [activeSection, setActiveSection] = useState('interests');

  const sections = [
    {
      id: 'interests',
      title: 'What Interests You?',
      subtitle: 'Define your ideal summer experience',
      icon: '🎯',
      status: 'exploring'
    },
    {
      id: 'collection',
      title: 'Your Program Collection',
      subtitle: 'Build your target list',
      icon: '📚',
      status: 'building'
    },
    {
      id: 'research',
      title: 'Research Tools',
      subtitle: 'Deep dive into programs',
      icon: '🔬',
      status: 'researching'
    },
    {
      id: 'essays',
      title: 'Essay Builder',
      subtitle: 'Draft your application essays',
      icon: '✍️',
      status: 'writing'
    },
    {
      id: 'timeline',
      title: 'Planning Timeline',
      subtitle: 'When to research and apply',
      icon: '📅',
      status: 'planning'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      exploring: 'bg-emerald-100 text-emerald-700',
      building: 'bg-emerald-100 text-emerald-700',
      researching: 'bg-sky-100 text-sky-700',
      writing: 'bg-violet-100 text-violet-600',
      planning: 'bg-amber-100 text-amber-600'
    };
    return colors[status] || 'bg-stone-100 text-stone-700';
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'interests':
        return <InterestProfile />;
      case 'collection':
        return <ProgramCollection />;
      case 'research':
        return <ResearchTools />;
      case 'essays':
        return <EssayBuilder />;
      case 'timeline':
        return <TimelinePlanning />;
      default:
        return <InterestProfile />;
    }
  };

  return (
    <CollegePrepProvider>
      <div className="min-h-screen bg-stone-50">
        {/* Header */}

            <div className="lg:col-span-2 space-y-4 lg:space-y-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-sky-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">B</span>
                </div>
                <h1 className="text-xl font-semibold text-slate-900">College Prep</h1>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-sm text-stone-600 border border-stone-300 rounded-md hover:bg-stone-50">
                  Find Programs
                </button>
                <button className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">
                  Save Progress
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Explorer Header */}
          <div className="bg-gradient-to-r from-emerald-600 via-emerald-600 to-sky-600 rounded-2xl p-6 mb-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold mb-1">Summer 2026 Explorer</h2>
                <p className="text-emerald-100">Discover Your Perfect Summer Experience</p>
              </div>
              <div className="flex gap-6 text-sm font-medium">
                <div>Rising Junior</div>
                <div>6 Months to Plan</div>
                <div>Build Your Story</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 xl:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">
              {/* Section Navigation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                      activeSection === section.id
                        ? 'border-emerald-300 bg-emerald-50 shadow-sm'
                        : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl">{section.icon}</span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(section.status)}`}>
                        {section.status}
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{section.title}</h3>
                    <p className="text-sm text-stone-600">{section.subtitle}</p>
                  </button>
                ))}
              </div>

              {/* Section Content */}
              <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                {renderSectionContent()}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              {/* Next Action */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    !
                  </div>
                  <h3 className="font-semibold text-emerald-700">Next Focus</h3>
                </div>
                <p className="text-sm text-emerald-600 mb-3">
                  Define your interests to get personalized program recommendations.
                </p>
                <button 
                  onClick={() => setActiveSection('interests')}
                  className="w-full px-3 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600"
                >
                  Start Exploring
                </button>
              </div>

              {/* Progress Stats */}
              <div className="bg-white border border-stone-200 rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Discovery Progress</h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="text-center p-3 bg-stone-50 rounded-lg">
                    <div className="text-lg font-bold text-slate-900">0</div>
                    <div className="text-xs text-stone-600">Programs Found</div>
                  </div>
                  <div className="text-center p-3 bg-stone-50 rounded-lg">
                    <div className="text-lg font-bold text-slate-900">25%</div>
                    <div className="text-xs text-stone-600">Interests Defined</div>
                  </div>
                </div>
                
                <div className="border-t border-stone-200 pt-3">
                  <div className="text-sm font-medium text-slate-900 mb-2">Status:</div>
                  <div className="space-y-1 text-xs">
                    <div className="text-amber-600">⚡ Just getting started</div>
                    <div className="text-stone-500">📋 Ready to explore interests</div>
                    <div className="text-stone-400">📚 Program research pending</div>
                  </div>
                </div>
              </div>

              {/* Explorer Tips */}
              <div className="bg-white border border-stone-200 rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Explorer Tips</h3>
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-3 rounded">
                  <ul className="text-xs text-emerald-700 space-y-1">
                    <li>• Start with reach programs - you never know!</li>
                    <li>• Don't stress about perfection - explore what excites you</li>
                    <li>• Good research now = better applications later</li>
                    <li>• Keep notes on everything - even "maybes" help</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CollegePrepProvider>
  );
};

export default CollegePrep;